import _ from 'underscore';

/**
 * `TLinked` maps each linked resource name to the type of its elements. For example, this response:
 *
 *   {
 *     elements: [{ id: 'abc', profileId: 'p1' }],
 *     links: { elements: { profileId: 'onDemandSocialProfiles.v1' } },
 *     linked: { 'onDemandSocialProfiles.v1': [{ id: 'p1', fullName: 'Ada' }] },
 *   }
 *
 * is a `NaptimeLinkedResponse<{ id: string; profileId: string }, { 'onDemandSocialProfiles.v1': { id: string; fullName: string } }>`,
 * and `legacyHydrate` turns each element into a `HydratedNaptimeElement` with its linked resource attached:
 *
 *   { id: 'abc', profileId: 'p1', 'onDemandSocialProfiles.v1': { id: 'p1', fullName: 'Ada' } }
 */
export type NaptimeLinkedResponse<TElement extends object, TLinked extends Record<string, object>> = {
  elements: Array<TElement>;
  links: { elements: Record<string, string> };
  linked: { [TResource in keyof TLinked]?: Array<TLinked[TResource]> };
};

// Each linked resource is attached under its resource name, or as `undefined` when the response has no match.
// `profiles.v0` links are the exception: they're attached under the link field (or `creator`), which this doesn't model.
export type HydratedNaptimeElement<TElement extends object, TLinked extends Record<string, object>> = TElement &
  Partial<TLinked>;

export const legacyHydrate = <TElement extends object, TLinked extends Record<string, object>>(
  naptimeResponse: NaptimeLinkedResponse<TElement, TLinked>
): Array<HydratedNaptimeElement<TElement, TLinked>> => {
  const linked = naptimeResponse.linked as Record<string, Array<{ id?: unknown }> | undefined>;
  // The elements are mutated in place, so the returned array is the same one with the linked resources attached.
  return _(naptimeResponse.elements as Array<Record<string, unknown>>).each(function (item) {
    _(naptimeResponse.links.elements).each(function (link, linkId) {
      /*
          For user profile, we get the creator of the post or we get the user
          who posted last to the thread. The profile.v0 from the linked object
          is an array with both these pieces of information. Hence this requires special treatment.
          The creator for the post is now stored in "creator" object and lastAnsweredBy details is
          updated to "lastAnsweredBy" object.
         */
      if (link === 'profiles.v0') {
        const relLinkId = linkId === 'creatorId' ? 'creator' : linkId;
        const userId = item[linkId];
        item[relLinkId] = _(linked[link]).where({ id: userId })[0];
      } else {
        item[link] = _(linked[link]).where({
          id: item[linkId],
        })[0];
      }
    });
  }) as Array<HydratedNaptimeElement<TElement, TLinked>>;
};
